import type { PageProgress } from '@idc-forms/client-app-domain/documents/document-meta'
import { usePDFSlick } from '@pdfslick/react'
import { useEffect, useState } from 'react'
import '@pdfslick/react/dist/pdf_viewer.css'
import { CapDocumentNotice } from './cap-document-notice'
import { CapText } from './cap-text'

/**
 * El visor de PDF de la previsualización.
 *
 * ## Por qué NO se monta el visor de `client-portal` tal cual
 *
 * E9 del contexto común dice «el visor que ya existe», y la librería y los parámetros de carga son
 * exactamente los de `features/client-portal/components/pdf-viewer/pdf-viewer.tsx`: el mismo
 * `usePDFSlick`, los mismos `getDocumentParams`, el mismo `onProgress`. Lo que no viaja es su
 * CHROME, y ahí el mismo E9 se contradice con lo que pide un renglón después.
 *
 * Aquel componente monta su propia barra de herramientas —zoom, input de página, «Descargar»,
 * «Imprimir», con los botones de la plataforma—, una columna de miniaturas y una barra de progreso
 * `fixed` que cruza la ventana ENTERA del navegador. Adentro de un marco de 393 px eso es interfaz
 * del back-office metida en una pantalla del cliente: el asesor abre esto justamente para ver lo
 * que el cliente ve, y el cliente no ve nada de eso. Además el componente no expone `pageNumber` ni
 * `numPages`, así que el «Página N de M» que E9 pide **en el subtítulo del header** no se puede
 * sacar de ahí sin modificarlo — y `client-portal` está fuera del alcance de esta tanda.
 *
 * Así que se replica la app: el documento y nada más, y la página se reporta hacia arriba.
 *
 * ## Dónde va el número de página
 *
 * En una **píldora flotante abajo a la izquierda**, y lo reporta hacia arriba: lo dibuja la ficha.
 *
 * ☠️ **Acá decía lo contrario, y el argumento era bueno**: «el PDF típico de esta app es un plano, y
 * una píldora flotante en la esquina tapa justo el dibujo que la persona vino a mirar». Lo que le
 * faltaba es que el subtítulo se lee UNA vez al abrir y este número cambia mientras se scrollea —
 * un dato que cambia arriba, lejos del ojo, no se ve—. La app ya se movió (T3/T4), la maqueta la
 * acompaña, y la previsualización no puede quedarse afirmando la forma anterior (ADR 0039).
 *
 * ## Las páginas separadas de la maqueta salen del MOTOR, no de un envoltorio
 *
 * Figma dibuja cada página como una tarjeta blanca sobre el gris del fondo, y la app lo pide con
 * `pageGap` + `contentPadding` + el color de fondo. Acá los tres se piden igual: el gap y el borde
 * de página son variables CSS de `pdf_viewer.css` (`--page-margin`, `--page-border`) que
 * `cap-preview.css` pisa con los MISMOS números que la app pasa al motor nativo (12 de aire entre
 * páginas), y la canaleta lateral de 16 la pone el envoltorio de este componente.
 *
 * ⚠️ **La canaleta NO puede ir como `padding` del contenedor de scroll**: pdf.js calcula el
 * `page-width` contra `container.clientWidth`, que INCLUYE el padding, así que las páginas saldrían
 * 32 px más anchas que el hueco y aparecería un scroll horizontal. Por eso hay un `div` intermedio:
 * el contenedor absoluto de PDFSlick mide el hueco ya recortado.
 *
 * ## Sin radio y sin sombra por página — y es el residuo asumido de la app
 *
 * En el teléfono las dibuja el motor nativo y no expone nada para pedírselas; falsificarlas
 * envolviendo el visor apaga el visor entero en Android (ADR 0034). Acá se podrían dibujar, y
 * justamente por eso no se dibujan: la previsualización mostraría un documento más prolijo que el
 * que el cliente tiene delante.
 *
 * ## El visor se monta desde el principio, y el progreso va ENCIMA
 *
 * La app puede dibujar la barra de progreso EN LUGAR del visor porque allá el archivo se baja
 * aparte y recién después se le entrega al motor. Acá el que baja es el propio `usePDFSlick` a
 * través de su `viewerRef`: si el visor no está montado, la descarga no arranca y la barra se queda
 * en cero para siempre. Por eso el aviso de carga es una capa superpuesta que se retira al terminar.
 */
export type CapPdfViewerProps = {
  /** La URL FIRMADA del documento. Vive cinco minutos. */
  url: string
  /** Reporta en qué página va, para el subtítulo del header. */
  onPageChange?: (page: PageProgress) => void
}

/**
 * Los mismos `getDocumentParams` del visor de la plataforma. Se copian con su valor y no se
 * «mejoran»: son los que ese visor tiene medidos contra los PDF que sube este back-office, y un PDF
 * que allá abre y acá no volvería la previsualización menos confiable que no tenerla.
 */
const DOCUMENT_PARAMS = {
  disableAutoFetch: false,
  disableFontFace: false,
  disableRange: false,
  disableStream: true,
  verbosity: 0
} as const

export function CapPdfViewer({ url, onPageChange }: CapPdfViewerProps) {
  const [loadedPercent, setLoadedPercent] = useState(0)

  const { isDocumentLoaded, viewerRef, usePDFSlickStore, PDFSlickViewer, error } = usePDFSlick(
    url,
    {
      // A lo ancho del marco, que es el `fitMode="width"` del visor nativo de la app.
      scaleValue: 'page-width',
      getDocumentParams: { ...DOCUMENT_PARAMS },
      onProgress: ({ total, loaded }) => setLoadedPercent(total > 0 ? (100 * loaded) / total : 0)
    }
  )

  const pageNumber = usePDFSlickStore((state) => state.pageNumber)
  const numPages = usePDFSlickStore((state) => state.numPages)

  /*
    El efecto y no un `onPageChanged`: `usePDFSlick` no tiene callback de página, publica el estado
    en su store. Se reporta sólo con el documento cargado porque antes `numPages` es 0, y un
    «Página 1 de 0» en el header es peor que ningún subtítulo.
  */
  useEffect(() => {
    if (!isDocumentLoaded || numPages < 1) return
    onPageChange?.({ current: pageNumber, total: numPages })
  }, [isDocumentLoaded, pageNumber, numPages, onPageChange])

  if (error) {
    return (
      <div className="cap-bg-neutral-100 h-full w-full px-4 py-3">
        <CapDocumentNotice title="No pudimos mostrar el documento" detail={motivo(error.name)} />
      </div>
    )
  }

  return (
    // A sangre: el gris del fondo llega hasta los bordes del teléfono y sólo las páginas quedan
    // metidas para adentro, como la maqueta y como la app.
    <div className="cap-bg-neutral-100 relative h-full w-full">
      {/* La canaleta del `contentPadding` de la app: 16 a los costados, 12 arriba y abajo. Va en
          este `div` y no en el contenedor de scroll — ver el ⚠️ del docblock. */}
      <div className="absolute inset-0 px-4 py-3">
        <div className="relative h-full w-full">
          <PDFSlickViewer viewerRef={viewerRef} usePDFSlickStore={usePDFSlickStore} />
        </div>
      </div>

      {isDocumentLoaded ? null : <Progreso percent={loadedPercent} />}
    </div>
  )
}

/**
 * Lo que pdf.js puede decir que salió mal, en palabras del cliente.
 *
 * Se lee el `name` de la excepción y no su `message`: el mensaje de pdf.js viene en inglés y
 * pensado para quien depura, y la regla de la casa es que la interfaz compara CÓDIGOS, nunca
 * textos. Lo que no está en la tabla cae al genérico en vez de filtrar el inglés a la pantalla.
 */
function motivo(name: string): string {
  if (name === 'InvalidPDFException') return 'El archivo está dañado o no es un PDF válido.'
  if (name === 'ResponseException') return 'No pudimos descargar el archivo.'
  if (name === 'PasswordException') return 'Este documento está protegido con contraseña.'

  return 'El visor no pudo abrir este archivo.'
}

/**
 * La barra de la app: bytes reales, no un spinner. Es la diferencia entre «está tardando» y «esto
 * se colgó» cuando alguien abre un plano pesado.
 *
 * Al 0 % todavía no hay bytes que contar —la firma se está resolviendo, la petición viaja— así que
 * dice «Preparando…» con la barra al 35 %, igual que la app cuando no conoce el total.
 */
function Progreso({ percent }: { percent: number }) {
  const conocido = percent > 0
  const redondeado = Math.round(percent)

  return (
    <div className="cap-bg-neutral-0 cap-border-neutral-200 absolute inset-x-4 inset-y-3 flex flex-col items-center justify-center gap-3 rounded-[var(--cap-radius-card)] border px-8">
      <div className="cap-bg-neutral-200 h-1.5 w-full overflow-hidden rounded-[var(--cap-radius-pill)]">
        <div
          className="cap-bg-brand-700 h-full rounded-[var(--cap-radius-pill)] transition-[width] duration-150 ease-out"
          style={{ width: conocido ? `${redondeado}%` : '35%' }}
        />
      </div>
      <CapText variant="caption" tone="muted">
        {conocido ? `Descargando… ${redondeado}%` : 'Preparando…'}
      </CapText>
    </div>
  )
}
